import { Lumber, LumberPropertySet } from '@paragon/contract-library';

import type { AppDispatch, RootState } from '../../store';
import { loadAdditionalLumberInformation } from '../../store/actions/loadActions';
import { getLumberPropertySetsKeyed } from '../../store/selectors/getLumberPropertySetsKeyedSelector';
import { getLumberPropertySetKey } from '../../store/slices/lumberPropertySetsSlice';

/** Design values by the names designers use for them, all in psi. */
export type DesignValueName = 'Fb' | 'Ft' | 'Fv' | 'Fc' | 'FcPerp' | 'E' | 'Emin';

const PROPERTIES: Record<DesignValueName, keyof LumberPropertySet> = {
  Fb: 'bending',
  Ft: 'tensionParallelToGrain',
  Fv: 'shearParallelToGrain',
  Fc: 'compressionParallelToGrain',
  FcPerp: 'compressionPerpendicularToGrain',
  E: 'modulusOfElasticity',
  Emin: 'modulusOfElasticityMin',
};

const ALL_DESIGN_VALUES = Object.keys(PROPERTIES) as DesignValueName[];

export type DesignValueLookup = (lumber: Lumber) => LumberPropertySet | null;

/**
 * The design values a lumber check depends on, the one that matters most first; none for checks a grade doesn't
 * change, such as plate checks.
 */
export function getGoverningDesignValues(checkName: string): DesignValueName[] {
  const name = checkName.toLowerCase();
  if (name.includes('shear')) return ['Fv'];
  if (name.includes('deflection')) return ['E'];
  if (name.includes('bearing')) return ['FcPerp'];
  if (name.includes('slenderness') || name.includes('buckling')) return ['Fc', 'Emin'];
  if (name.includes('bending') || name.includes('axial')) return ['Fb', 'Ft', 'Fc'];
  if (name.includes('tension')) return ['Ft'];
  if (name.includes('compression')) return ['Fc', 'Emin'];
  return [];
}

export function readDesignValues(set: LumberPropertySet, names: readonly DesignValueName[] = ALL_DESIGN_VALUES) {
  return Object.fromEntries(names.map((name) => [name, Number(set[PROPERTIES[name]])])) as Record<
    DesignValueName,
    number
  >;
}

const lumberKey = (lumber: Lumber) => getLumberPropertySetKey(lumber);

/**
 * The lumber among `options` that is stronger than `current` where it matters: higher on the first of `names`, and
 * lower on none of them, strongest first. `null` when `current`'s values aren't known, so nothing can be compared.
 */
export function rankStrongerLumber(
  current: Lumber,
  options: readonly Lumber[],
  names: readonly DesignValueName[],
  lookup: DesignValueLookup,
) {
  const currentSet = lookup(current);
  if (currentSet == null || names.length === 0) return null;
  const currentValues = readDesignValues(currentSet, names);
  const seen = new Set([lumberKey(current)]);

  return options
    .flatMap((option) => {
      const key = lumberKey(option);
      const set = seen.has(key) ? null : lookup(option);
      seen.add(key);
      if (set == null) return [];
      const values = readDesignValues(set, names);
      const stronger =
        values[names[0]] > currentValues[names[0]] && names.every((name) => values[name] >= currentValues[name]);
      return stronger ? [{ lumber: option, values }] : [];
    })
    .sort((a, b) => b.values[names[0]] - a.values[names[0]]);
}

/**
 * The design values that would drop going from `before` to `after`, such as "Fv 175 → 150 psi".
 */
export function describeWeakerDesignValues(before: LumberPropertySet, after: LumberPropertySet) {
  const was = readDesignValues(before);
  const now = readDesignValues(after);
  return ALL_DESIGN_VALUES.filter((name) => now[name] < was[name]).map(
    (name) => `${name} ${was[name]} → ${now[name]} psi`,
  );
}

const CONCURRENT_LOADS = 6;

/**
 * Loads the design values of `lumbers` the app hasn't loaded yet, as the properties panel does for one member, and
 * returns a lookup for them.
 */
export async function loadDesignValues(
  dispatch: AppDispatch,
  getState: () => RootState,
  lumbers: readonly Lumber[],
): Promise<DesignValueLookup> {
  const unique = [...new Map(lumbers.map((lumber) => [lumberKey(lumber), lumber])).values()];
  for (let index = 0; index < unique.length; index += CONCURRENT_LOADS)
    await Promise.all(
      unique
        .slice(index, index + CONCURRENT_LOADS)
        .map((lumber) => dispatch(loadAdditionalLumberInformation(lumber)).catch(() => {})),
    );
  return (lumber) => getLumberPropertySetsKeyed(getState())[lumberKey(lumber)] ?? null;
}
